/**
 * SegmentedControl — options in a track with a highlight pill that slides to
 * the selection (springs; instant under reduced motion). Measures each option,
 * so labels of any width work. Keyboard: options are tabs; arrows move.
 *
 *   <SegmentedControl
 *     options={[{ value: 'over', label: 'Over' }, { value: 'under', label: 'Under' }]}
 *     value={side} onChange={setSide} size="sm" />
 */
import React, { useEffect, useRef, useState } from 'react';
import { View, Text, Pressable, Animated, StyleSheet } from 'react-native';
import { IS_WEB, spring, webTransition } from '../../utils/motion';

export default function SegmentedControl({ options = [], value, onChange, size = 'md', style, accent = 'rgba(99,102,241,0.32)', accentBorder = 'rgba(129,140,248,0.6)', accessibilityLabel }) {
    const [layouts, setLayouts] = useState({});
    const x = useRef(new Animated.Value(0)).current;
    const w = useRef(new Animated.Value(0)).current;
    const placed = useRef(false);
    const sel = layouts[value];

    useEffect(() => {
        if (!sel) return;
        if (!placed.current) {
            // First placement: jump, don't slide in from the left edge.
            x.setValue(sel.x);
            w.setValue(sel.width);
            placed.current = true;
            return;
        }
        Animated.parallel([
            spring(x, sel.x, 'snappy', { useNative: false }),
            spring(w, sel.width, 'snappy', { useNative: false }),
        ]).start();
    }, [sel?.x, sel?.width]);

    const sm = size === 'sm';
    const idx = options.findIndex((o) => o.value === value);
    const move = (d) => {
        const next = options[(idx + d + options.length) % options.length];
        if (next) onChange?.(next.value);
    };

    return (
        <View
            style={[styles.track, sm && styles.trackSm, style]}
            role="tablist"
            aria-label={accessibilityLabel}
            {...(IS_WEB ? { onKeyDown: (e) => { if (e.key === 'ArrowRight') move(1); if (e.key === 'ArrowLeft') move(-1); } } : {})}
        >
            {sel ? (
                <Animated.View
                    style={[styles.pill, sm && styles.pillSm, { backgroundColor: accent, borderColor: accentBorder, left: x, width: w, pointerEvents: 'none' }]}
                />
            ) : null}
            {options.map((o) => {
                const active = o.value === value;
                return (
                    <Pressable
                        key={o.value}
                        role="tab"
                        aria-selected={active}
                        onPress={() => !active && onChange?.(o.value)}
                        onLayout={(e) => {
                            const { x: lx, width } = e.nativeEvent.layout;
                            // measure() reports x from the border edge; the pill's left is from the padding edge.
                            const px = lx - 1;
                            setLayouts((prev) => (prev[o.value]?.x === px && prev[o.value]?.width === width ? prev : { ...prev, [o.value]: { x: px, width } }));
                        }}
                        style={({ hovered, pressed }) => [
                            styles.option, sm && styles.optionSm,
                            webTransition(['transform', 'background-color'], 'fast'),
                            hovered && !active && styles.optionHover,
                            pressed && { transform: [{ scale: 0.96 }] },
                        ]}
                    >
                        <Text style={[styles.label, sm && styles.labelSm, active && styles.labelActive, webTransition(['color'], 'fast')]}>
                            {o.label}
                            {Number.isFinite(o.count) ? <Text style={styles.count}>{`  ${o.count}`}</Text> : null}
                        </Text>
                    </Pressable>
                );
            })}
        </View>
    );
}

const styles = StyleSheet.create({
    track: {
        flexDirection: 'row',
        alignSelf: 'flex-start',
        padding: 3,
        borderRadius: 10,
        backgroundColor: 'rgba(255,255,255,0.05)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.1)',
        position: 'relative',
    },
    trackSm: { padding: 2, borderRadius: 8 },
    pillSm: { top: 2, bottom: 2, borderRadius: 6 },
    pill: {
        position: 'absolute',
        top: 3,
        bottom: 3,
        borderRadius: 7,
        borderWidth: 1,
    },
    option: {
        minHeight: 34,
        minWidth: 56,
        paddingHorizontal: 14,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 7,
        zIndex: 1,
    },
    optionSm: { minHeight: 30, minWidth: 48, paddingHorizontal: 12 },
    optionHover: { backgroundColor: 'rgba(255,255,255,0.05)' },
    label: { color: 'rgba(255,255,255,0.55)', fontSize: 13, fontWeight: '700' },
    labelSm: { fontSize: 12 },
    labelActive: { color: '#fff' },
    count: { color: 'rgba(255,255,255,0.45)', fontWeight: '600' },
});
